'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RefinanceCalculator() {
  const t = useTranslations("tools.refinanceCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Calculator state
  const [loanType, setLoanType] = useState<'balance' | 'original'>('balance');
  const [inputs, setInputs] = useState({
    remainingBalance: 250000,
    currentPayment: 1800,
    originalAmount: 300000,
    originalTerm: 30,
    yearsRemaining: 20,
    monthsRemaining: 0,
    currentRate: 7,
    newTerm: 20,
    newRate: 6,
    points: 2,
    closingCosts: 1500,
    cashOut: 0,
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Colors
  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#16a34a',
    danger: '#dc2626',
    warning: '#f59e0b',
  };

  // Calculation functions
  const calculateMonthlyPayment = (principal: number, annualRate: number, months: number): number => {
    if (annualRate === 0) return principal / months;
    const monthlyRate = annualRate / 100 / 12;
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
  };

  const calculateRemainingBalance = (originalAmount: number, annualRate: number, originalMonths: number, paidMonths: number): number => {
    if (annualRate === 0) {
      return originalAmount * (1 - paidMonths / originalMonths);
    }
    const monthlyRate = annualRate / 100 / 12;
    const monthlyPayment = calculateMonthlyPayment(originalAmount, annualRate, originalMonths);
    const remainingMonths = originalMonths - paidMonths;
    return monthlyPayment * (Math.pow(1 + monthlyRate, remainingMonths) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, remainingMonths));
  };

  const formatCurrency = (value: number): string => {
    return '$' + Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatCurrencyWithSign = (value: number): string => {
    const sign = value >= 0 ? '+' : '-';
    return sign + formatCurrency(value);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      let remainingBalance: number;
      let currentMonthlyPayment: number;
      let remainingMonths: number;

      if (loanType === 'balance') {
        remainingBalance = inputs.remainingBalance;
        currentMonthlyPayment = inputs.currentPayment;

        if (inputs.currentRate > 0) {
          const monthlyRate = inputs.currentRate / 100 / 12;
          remainingMonths = Math.log(currentMonthlyPayment / (currentMonthlyPayment - remainingBalance * monthlyRate)) / Math.log(1 + monthlyRate);
        } else {
          remainingMonths = remainingBalance / currentMonthlyPayment;
        }
      } else {
        const originalMonths = inputs.originalTerm * 12;
        remainingMonths = inputs.yearsRemaining * 12 + inputs.monthsRemaining;
        const paidMonths = originalMonths - remainingMonths;

        remainingBalance = calculateRemainingBalance(inputs.originalAmount, inputs.currentRate, originalMonths, paidMonths);
        currentMonthlyPayment = calculateMonthlyPayment(inputs.originalAmount, inputs.currentRate, originalMonths);
      }

      const newLoanAmount = remainingBalance + inputs.cashOut;
      const pointsCost = newLoanAmount * (inputs.points / 100);
      const totalUpfrontCosts = pointsCost + inputs.closingCosts;

      const newMonths = inputs.newTerm * 12;
      const newMonthlyPayment = calculateMonthlyPayment(newLoanAmount, inputs.newRate, newMonths);

      const monthlySavings = currentMonthlyPayment - newMonthlyPayment;
      const breakEvenMonths = monthlySavings > 0 ? Math.ceil(totalUpfrontCosts / monthlySavings) : Infinity;

      const currentTotalInterest = (currentMonthlyPayment * remainingMonths) - remainingBalance;
      const newTotalInterest = (newMonthlyPayment * newMonths) - newLoanAmount;
      const interestDifference = currentTotalInterest - newTotalInterest;

      const currentTotalPaid = currentMonthlyPayment * remainingMonths;
      const newTotalPaid = (newMonthlyPayment * newMonths) + totalUpfrontCosts;
      const totalDifference = currentTotalPaid - newTotalPaid;

      let recommendationType: 'beneficial' | 'longBreakEven' | 'lowerPaymentOnly' | 'notRecommended';
      if (totalDifference > 0 && breakEvenMonths < 60) {
        recommendationType = 'beneficial';
      } else if (totalDifference > 0 && breakEvenMonths >= 60) {
        recommendationType = 'longBreakEven';
      } else if (monthlySavings > 0 && totalDifference < 0) {
        recommendationType = 'lowerPaymentOnly';
      } else {
        recommendationType = 'notRecommended';
      }

      setResults({
        newMonthlyPayment,
        monthlySavings,
        breakEvenMonths,
        totalUpfrontCosts,
        currentMonthlyPayment,
        currentTotalInterest,
        newTotalInterest,
        interestDifference,
        currentTotalPaid,
        newTotalPaid,
        totalDifference,
        recommendationType,
      });

    } catch (err) {
      setError(t('errorMessage'));
    }
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const,
  };

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const toolH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formSectionStyle = {
    background: '#f8fafc',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    border: '2px solid #e2e8f0',
  };

  const sectionH3Style = {
    margin: '0 0 1.5rem 0',
    fontSize: '1.2rem',
    color: '#1e293b',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const formGroupStyle = {
    marginBottom: '1.25rem',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#334155',
    fontSize: '0.95rem',
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const,
  };

  const selectStyle = {
    ...inputStyle,
  };

  const inputWithPrefixContainerStyle = {
    position: 'relative' as const,
  };

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
  };

  const inputSuffixStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
  };

  const inputWithPrefixStyle = {
    ...inputStyle,
    paddingLeft: '2rem',
  };

  const inputWithSuffixStyle = {
    ...inputStyle,
    paddingRight: '2.5rem',
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
    },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = {
    padding: '2rem',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderTop: '3px solid #22c55e',
  };

  const resultsH3Style = {
    margin: '0 0 1.5rem 0',
    fontSize: '1.5rem',
    color: '#166534',
    textAlign: 'center' as const,
  };

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const resultCardStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    borderLeft: '4px solid #2563eb',
  };

  const resultCardHighlightStyle = {
    ...resultCardStyle,
    borderLeftColor: '#f59e0b',
    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
  };

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.9rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500,
  };

  const resultValueStyle = {
    display: 'block',
    fontSize: '1.8rem',
    fontWeight: 700,
    color: '#1e293b',
  };

  const comparisonTableStyle = {
    background: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden' as const,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
  };

  const thStyle = {
    background: '#1e293b',
    color: 'white',
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: 600,
  };

  const tdStyle = {
    padding: '1rem',
    borderBottom: '1px solid #e2e8f0',
  };

  const recommendationStyle = {
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    border: '2px solid #2563eb',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginTop: '1.5rem',
  };

  const recommendationH4Style = {
    margin: '0 0 0.75rem 0',
    color: '#1e40af',
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const recommendationPStyle = {
    margin: 0,
    color: '#1e293b',
    lineHeight: 1.6,
  };

  const errorMessageStyle = {
    background: '#fef2f2',
    border: '2px solid #ef4444',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary,
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'all 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolH2Style}>🏠 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            {error && (
              <div style={errorMessageStyle}>
                {error}
              </div>
            )}

            <div style={formGridStyle}>
              <div style={formSectionStyle}>
                <h3 style={sectionH3Style}>
                  <span style={{ fontSize: '1.4rem' }}>📋</span> {t('currentLoanTitle')}
                </h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="loan-type">{t('loanTypeLabel')}</label>
                  <select
                    id="loan-type"
                    style={selectStyle}
                    value={loanType}
                    onChange={(e) => setLoanType(e.target.value as 'balance' | 'original')}
                  >
                    <option value="balance">{t('loanTypeBalance')}</option>
                    <option value="original">{t('loanTypeOriginal')}</option>
                  </select>
                </div>

                {loanType === 'balance' ? (
                  <>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="remaining-balance">{t('remainingBalanceLabel')}</label>
                      <div style={inputWithPrefixContainerStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          id="remaining-balance"
                          style={inputWithPrefixStyle}
                          value={inputs.remainingBalance}
                          onChange={(e) => setInputs({...inputs, remainingBalance: parseFloat(e.target.value) || 0})}
                          step="1000"
                          min="0"
                          required
                        />
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="current-payment">{t('currentPaymentLabel')}</label>
                      <div style={inputWithPrefixContainerStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          id="current-payment"
                          style={inputWithPrefixStyle}
                          value={inputs.currentPayment}
                          onChange={(e) => setInputs({...inputs, currentPayment: parseFloat(e.target.value) || 0})}
                          step="10"
                          min="0"
                          required
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="original-amount">{t('originalAmountLabel')}</label>
                      <div style={inputWithPrefixContainerStyle}>
                        <span style={inputPrefixStyle}>$</span>
                        <input
                          type="number"
                          id="original-amount"
                          style={inputWithPrefixStyle}
                          value={inputs.originalAmount}
                          onChange={(e) => setInputs({...inputs, originalAmount: parseFloat(e.target.value) || 0})}
                          step="1000"
                          min="0"
                        />
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="original-term">{t('originalTermLabel')}</label>
                      <div style={inputWithPrefixContainerStyle}>
                        <input
                          type="number"
                          id="original-term"
                          style={inputWithSuffixStyle}
                          value={inputs.originalTerm}
                          onChange={(e) => setInputs({...inputs, originalTerm: parseFloat(e.target.value) || 0})}
                          step="1"
                          min="1"
                          max="50"
                        />
                        <span style={inputSuffixStyle}>{t('yearsUnit')}</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="years-remaining">{t('yearsRemainingLabel')}</label>
                      <div style={inputWithPrefixContainerStyle}>
                        <input
                          type="number"
                          id="years-remaining"
                          style={inputWithSuffixStyle}
                          value={inputs.yearsRemaining}
                          onChange={(e) => setInputs({...inputs, yearsRemaining: parseFloat(e.target.value) || 0})}
                          step="1"
                          min="0"
                          max="50"
                        />
                        <span style={inputSuffixStyle}>{t('yearsUnit')}</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="months-remaining">{t('monthsRemainingLabel')}</label>
                      <div style={inputWithPrefixContainerStyle}>
                        <input
                          type="number"
                          id="months-remaining"
                          style={inputWithSuffixStyle}
                          value={inputs.monthsRemaining}
                          onChange={(e) => setInputs({...inputs, monthsRemaining: parseFloat(e.target.value) || 0})}
                          step="1"
                          min="0"
                          max="11"
                        />
                        <span style={inputSuffixStyle}>{t('monthsUnit')}</span>
                      </div>
                    </div>
                  </>
                )}

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="current-rate">{t('currentRateLabel')}</label>
                  <div style={inputWithPrefixContainerStyle}>
                    <input
                      type="number"
                      id="current-rate"
                      style={inputWithSuffixStyle}
                      value={inputs.currentRate}
                      onChange={(e) => setInputs({...inputs, currentRate: parseFloat(e.target.value) || 0})}
                      step="0.01"
                      min="0"
                      max="30"
                      required
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>
              </div>

              <div style={formSectionStyle}>
                <h3 style={sectionH3Style}>
                  <span style={{ fontSize: '1.4rem' }}>✨</span> {t('newLoanTitle')}
                </h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="new-term">{t('newTermLabel')}</label>
                  <div style={inputWithPrefixContainerStyle}>
                    <input
                      type="number"
                      id="new-term"
                      style={inputWithSuffixStyle}
                      value={inputs.newTerm}
                      onChange={(e) => setInputs({...inputs, newTerm: parseFloat(e.target.value) || 0})}
                      step="1"
                      min="1"
                      max="50"
                      required
                    />
                    <span style={inputSuffixStyle}>{t('yearsUnit')}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="new-rate">{t('newRateLabel')}</label>
                  <div style={inputWithPrefixContainerStyle}>
                    <input
                      type="number"
                      id="new-rate"
                      style={inputWithSuffixStyle}
                      value={inputs.newRate}
                      onChange={(e) => setInputs({...inputs, newRate: parseFloat(e.target.value) || 0})}
                      step="0.01"
                      min="0"
                      max="30"
                      required
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="points">{t('pointsLabel')}</label>
                  <div style={inputWithPrefixContainerStyle}>
                    <input
                      type="number"
                      id="points"
                      style={inputWithSuffixStyle}
                      value={inputs.points}
                      onChange={(e) => setInputs({...inputs, points: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      min="0"
                      max="10"
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="closing-costs">{t('closingCostsLabel')}</label>
                  <div style={inputWithPrefixContainerStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="closing-costs"
                      style={inputWithPrefixStyle}
                      value={inputs.closingCosts}
                      onChange={(e) => setInputs({...inputs, closingCosts: parseFloat(e.target.value) || 0})}
                      step="100"
                      min="0"
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="cash-out">{t('cashOutLabel')}</label>
                  <div style={inputWithPrefixContainerStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="cash-out"
                      style={inputWithPrefixStyle}
                      value={inputs.cashOut}
                      onChange={(e) => setInputs({...inputs, cashOut: parseFloat(e.target.value) || 0})}
                      step="1000"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" style={calculateButtonStyle}>
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultsGridStyle}>
                <div style={resultCardHighlightStyle}>
                  <span style={resultLabelStyle}>{t('newPaymentLabel')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.newMonthlyPayment)}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('monthlySavingsLabel')}</span>
                  <span style={{...resultValueStyle, color: results.monthlySavings > 0 ? colors.success : colors.danger}}>
                    {formatCurrencyWithSign(results.monthlySavings)}
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('breakEvenLabel')}</span>
                  <span style={resultValueStyle}>
                    {results.breakEvenMonths === Infinity ? t('notApplicable') :
                      (Math.floor(results.breakEvenMonths / 12) > 0
                        ? `${Math.floor(results.breakEvenMonths / 12)}${t('yearsShort')} ${results.breakEvenMonths % 12}${t('monthsShort')}`
                        : `${results.breakEvenMonths} ${t('monthsUnit')}`)}
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('totalCostsLabel')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalUpfrontCosts)}</span>
                </div>
              </div>

              <div style={comparisonTableStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('comparisonHeader')}</th>
                      <th style={thStyle}>{t('currentLoanHeader')}</th>
                      <th style={thStyle}>{t('newLoanHeader')}</th>
                      <th style={thStyle}>{t('differenceHeader')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={tdStyle}><strong>{t('monthlyPaymentRow')}</strong></td>
                      <td style={tdStyle}>{formatCurrency(results.currentMonthlyPayment)}</td>
                      <td style={tdStyle}>{formatCurrency(results.newMonthlyPayment)}</td>
                      <td style={{...tdStyle, color: results.monthlySavings > 0 ? colors.success : colors.danger}}>
                        {formatCurrencyWithSign(results.monthlySavings)}
                      </td>
                    </tr>
                    <tr>
                      <td style={tdStyle}><strong>{t('totalInterestRow')}</strong></td>
                      <td style={tdStyle}>{formatCurrency(results.currentTotalInterest)}</td>
                      <td style={tdStyle}>{formatCurrency(results.newTotalInterest)}</td>
                      <td style={{...tdStyle, color: results.interestDifference > 0 ? colors.success : colors.danger}}>
                        {formatCurrencyWithSign(results.interestDifference)}
                      </td>
                    </tr>
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={{...tdStyle, borderBottom: 'none'}}><strong>{t('totalPaidRow')}</strong></td>
                      <td style={{...tdStyle, borderBottom: 'none'}}>{formatCurrency(results.currentTotalPaid)}</td>
                      <td style={{...tdStyle, borderBottom: 'none'}}>{formatCurrency(results.newTotalPaid)}</td>
                      <td style={{...tdStyle, borderBottom: 'none', color: results.totalDifference > 0 ? colors.success : colors.danger}}>
                        {formatCurrencyWithSign(results.totalDifference)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={recommendationStyle}>
                <h4 style={recommendationH4Style}>
                  💡 {t('recommendationTitle')}
                </h4>
                <p style={recommendationPStyle}>
                  {results.recommendationType === 'beneficial' &&
                    t('recommendationBeneficial', {
                      monthlySavings: formatCurrency(results.monthlySavings),
                      totalDifference: formatCurrency(results.totalDifference),
                      breakEvenMonths: results.breakEvenMonths.toString()
                    })}
                  {results.recommendationType === 'longBreakEven' &&
                    t('recommendationLongBreakEven', {
                      totalDifference: formatCurrency(results.totalDifference),
                      breakEvenMonths: results.breakEvenMonths.toString()
                    })}
                  {results.recommendationType === 'lowerPaymentOnly' &&
                    t('recommendationLowerPaymentOnly', {
                      monthlySavings: formatCurrency(results.monthlySavings),
                      totalDifference: formatCurrency(Math.abs(results.totalDifference))
                    })}
                  {results.recommendationType === 'notRecommended' &&
                    t('recommendationNotRecommended', {
                      monthlySavings: formatCurrency(Math.abs(results.monthlySavings)),
                      totalDifference: formatCurrency(Math.abs(results.totalDifference))
                    })}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep1Title')}</strong> {t('howToStep1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep2Title')}</strong> {t('howToStep2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep3Title')}</strong> {t('howToStep3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep4Title')}</strong> {t('howToStep4Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep5Title')}</strong> {t('howToStep5Description')}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t('insightsBreakEvenTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsBreakEvenDescription')}</p>

        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t('insightsTotalCostTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsTotalCostDescription')}</p>

        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t('insightsRateDropTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsRateDropDescription')}</p>

        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t('insightsTermChangeTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsTermChangeDescription')}</p>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t('detailsWhenToRefinanceTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('detailsWhenToRefinance1')}</li>
          <li style={listItemStyle}>{t('detailsWhenToRefinance2')}</li>
          <li style={listItemStyle}>{t('detailsWhenToRefinance3')}</li>
          <li style={listItemStyle}>{t('detailsWhenToRefinance4')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t('detailsClosingCostsTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsClosingCostsDescription')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('detailsClosingCost1')}</li>
          <li style={listItemStyle}>{t('detailsClosingCost2')}</li>
          <li style={listItemStyle}>{t('detailsClosingCost3')}</li>
          <li style={listItemStyle}>{t('detailsClosingCost4')}</li>
          <li style={listItemStyle}>{t('detailsClosingCost5')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t('detailsQualificationTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsQualificationDescription')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('detailsQualification1')}</li>
          <li style={listItemStyle}>{t('detailsQualification2')}</li>
          <li style={listItemStyle}>{t('detailsQualification3')}</li>
          <li style={listItemStyle}>{t('detailsQualification4')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{...h2Style, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{...h2Style, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{...h2Style, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{...h2Style, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{...h2Style, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={{...h2Style, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('faq6Question')}</h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.bankrate.com/mortgages/refinance-calculator/" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nerdwallet.com/mortgages/refinance-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.consumerfinance.gov/owning-a-home/mortgage-refinance/" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('reference3')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}